import * as React from 'react';
import * as _ from 'underscore';

import { ButtonType, ButtonWidget } from '@tableau/widgets';

import { Messages } from '../../../codegen/Localize';
import { BuyLicensesTelemetryEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { LicensesService } from '../../../ts/services/LicensesService';
import { ServerService } from '../../../ts/services/ServerService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../ts/services/SiteAuthenticationSettingsService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { WindowLocationService } from '../../../ts/services/WindowLocationService';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { ConnectedExplorer } from '../Explorer/ConnectedExplorer';
import { LicenseInfoBar } from '../Header/LicenseInfoBar';
import { SpaceHeaderWithTotalItemsCount } from '../Header/SpaceHeaderWithTotalItemsCount';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { SiteUserAuthenticationProviderContext } from './UserAuthenticationProviderContext';

export interface AllSiteUsersSpaceOwnProps extends ReactDirectiveProps {
  readonly urls: SpacesSiteUrls;
}

export interface AllSiteUsersAngularProps {
  readonly browserTitleService: BrowserTitleService;
  readonly telemetry: Telemetry;
  readonly licensesService: LicensesService;
  readonly siteAuthSettingsService: SiteAuthenticationSettingsService;
  readonly windowLocationService: WindowLocationService;
  readonly serverService: ServerService;
}

export interface AllSiteUsersSpaceStateProps {
  readonly isBusyFetchingItems: boolean;
  readonly isSessionMultiSite: boolean;
  readonly numberOfFetchedUsers: number;
  readonly isSessionServerAdmin: boolean;
  readonly canSessionUserManageUsers: boolean;
}

export interface AllSiteUsersSpaceDispatchProps {
  readonly requestSpaceContents: () => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
  readonly addSiteUsers: () => void;
}

export type AllSiteUsersSpaceProps = AllSiteUsersSpaceOwnProps & AllSiteUsersAngularProps & AllSiteUsersSpaceStateProps & AllSiteUsersSpaceDispatchProps;

export interface AllSiteUsersSpaceState {
  readonly displayLicensingBar: boolean;
  readonly hasSiteLicensingInfo: boolean;
  readonly renderChildren: boolean;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
  readonly buyLicensesEnabled: boolean;
}

export class AllSiteUsersSpace extends React.PureComponent<AllSiteUsersSpaceProps, AllSiteUsersSpaceState> {
  public constructor(props: AllSiteUsersSpaceProps) {
    super(props);
    this.state = { displayLicensingBar: undefined, hasSiteLicensingInfo: false, renderChildren: false, siteAuthenticationSettings: undefined, buyLicensesEnabled: false };
  }

  public componentDidMount() {
    this.props.updateBreadcrumbs([getSiteUsersSpaceBreadcrumb(this.props.urls, this.props.isSessionMultiSite)]);
    this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.props.isSessionMultiSite ? Messages.siteUsers() : Messages.users() });
    this.props.requestSpaceContents();
    this.updateSiteLicenseConsumptionInfo();
    this.getSiteAuthenticationSettings();
  }

  public componentDidUpdate() {
    if (!this.state.renderChildren && !_.isUndefined(this.state.displayLicensingBar) && !_.isUndefined(this.state.siteAuthenticationSettings)) {
      this.setState({renderChildren: true});
    }
  }

  public render(): JSX.Element {
    if (!this.state.renderChildren) {
      return null;
    }
    return(
      <div className={ SpaceContainerStyles.SpaceContainer }>
        <SpaceHeaderWithTotalItemsCount
          onClick={this.onSpaceHeaderClick}
          label={this.props.isSessionMultiSite ? Messages.siteUsers() : Messages.users()}
          testId={'site-users'}
          count={this.props.numberOfFetchedUsers}
          countUpToDate={!this.props.isBusyFetchingItems}
          licenseInfoBar={this.licenseBar}
        />
        <div className={ SpaceContainerStyles.SubplaceArea }>
          <SiteUserAuthenticationProviderContext.Provider value={this.authenticationProvider}>
            <ConnectedExplorer
              angularContext={this.props.angularContext}
              supportedDisplayModes={['list']}
              telemetry={this.props.telemetry}
              toolbarProps={{
                supportFilterPanel: true,
                toolbarVisibility: 'enabled',
                toolbarContentSpecificControls: { beginning: this.addUserButton() }
              }}
              emptyStateProps={{
                primaryMessage: Messages.usersEmptyMessage()
              }}
            />
          </SiteUserAuthenticationProviderContext.Provider>
        </div>
      </div>
    );
  }

  private authenticationProvider = (user: ISiteUserInfo) => {
    const provider = this.props.siteAuthSettingsService.findProvider(this.state.siteAuthenticationSettings, user.authenticationProviderId);
    if (provider) {
      return {
        name: this.props.siteAuthSettingsService.getProviderDisplayName(provider),
        isEnabled: provider.isEnabled
      };
    } else {
      return {
        name: Messages.unspecified(),
        isEnabled: false
      };
    }
  }

  private getSiteAuthenticationSettings = async () => {
    const siteAuthenticationSettings = await this.props.siteAuthSettingsService.getSiteAuthenticationSettings();
    this.setState({siteAuthenticationSettings});
  }

  private updateSiteLicenseConsumptionInfo = async () => {
    const hasSiteLicensingInfo = await this.props.licensesService.hasSiteLicensingConsumptionInfo();
    const showLicenseInfoBarForSingleSiteServer = !this.props.isSessionMultiSite
      && this.props.isSessionServerAdmin;
    const displayLicensingBar = hasSiteLicensingInfo || showLicenseInfoBarForSingleSiteServer;
    const buyLicensesEnabled = this.props.serverService.isBuyLicensesEnabled();

    this.setState({
      hasSiteLicensingInfo,
      displayLicensingBar,
      buyLicensesEnabled,
    });
  }

  private licenseBar = (): JSX.Element => {
    return (
      <div style={{display: 'flex'}}>
        {this.state.displayLicensingBar && (
          <LicenseInfoBar
            hasSiteLicensingInfo={this.state.hasSiteLicensingInfo}
            licensesService={this.props.licensesService}
          />
        )}
        <div style={{flex: 1}}/>
        {this.state.buyLicensesEnabled && (
          <BuyLicensesButton
            windowLocationService={this.props.windowLocationService}
            telemetry={this.props.telemetry}
          />
        )}
      </div>
    );
  }

  private onSpaceHeaderClick = () => {
    this.props.requestSpaceContents();
  }

  private addUserButton = () => {
    if (this.props.canSessionUserManageUsers) {
      const handleClick = () => this.props.addSiteUsers();
      return (
        <ButtonWidget
          children={Messages.addUsers()}
          buttonType={ButtonType.Outline}
          handleClick={handleClick}
          tb-test-id={'action-add-users'}
          testId={'action-add-users'}/>
      );
    }
    return null;
  }
}

interface BuyLicensesButtonProps {
  readonly windowLocationService: WindowLocationService;
  readonly telemetry: Telemetry;
}

class BuyLicensesButton extends React.PureComponent<BuyLicensesButtonProps> {
  public constructor(props: BuyLicensesButtonProps) {
    super(props);
  }

  public shouldComponentUpdate(nextProps: BuyLicensesButtonProps) {
    return false;
  }

  public componentDidMount() {
    this.props.telemetry.sendEvent<BuyLicensesTelemetryEvent>({
      type: TelemetryEvents.BuyLicenses,
      action: 'buy-licenses-button-displayed',
    });
  }

  public render(): JSX.Element {
    return (
      <ButtonWidget
        children={Messages.buyLicenses()}
        buttonType={ButtonType.Outline}
        handleClick={this.handleClick}
        tb-test-id={'buy-licenses'}
        testId={'buy-licenses'} />
    );
  }

  private handleClick = () => {
    this.props.telemetry.sendEvent<BuyLicensesTelemetryEvent>({
      type: TelemetryEvents.BuyLicenses,
      action: 'buy-licenses-button-clicked',
    });
    this.props.windowLocationService.open('https://www.tableau.com/about/contact#contact_sales');
  }
}

export function getSiteUsersSpaceBreadcrumb(urls: SpacesSiteUrls, multiSite: boolean): BreadcrumbStateInfo {
  return {
    id: urls && urls.usersUrl(),
    text: multiSite ? Messages.allSiteUsers() : Messages.allUsers(),
    href: urls && urls.usersUrl(),
    testId: 'site-users-link'
  };
}
